import type { FC, SVGProps } from "react"
import { Fragment } from "react"

import type { Point2d } from "@logic/lib/2d"
import { studioToScreen } from "@logic/lib/design"
import type { Identified } from "@logic/lib/utils"
import { useRecoilValue } from "recoil"

import { zoomState } from "~/containers/LegacyDesigner/state/view"
import { CoordinateTooltip } from "~/containers/LegacyDesigner/Studio/Overlays/CoordinateTooltip"
import { getRainbow } from "~/utils/getRainbow"

import { nodeLabelSearchTermsState } from "../DesignerDebugPanel"

export const NodeGroups: FC<{
  nodeGroups: (Partial<Identified> & Point2d)[][]
  makeProps?: (options: {
    point: Point2d
    groupIdx: number
    memberIdx: number
  }) => SVGProps<SVGCircleElement>
  shouldLabel?: boolean
}> = ({ nodeGroups, makeProps, shouldLabel = true }) => {
  const zoom = useRecoilValue(zoomState)

  const nodeLabelSearchTerms = useRecoilValue(nodeLabelSearchTermsState)

  return (
    <>
      {nodeGroups.map((points, i, { length }) => {
        const r = (length - i + 1) * 3
        const opacity = (90 - r * 2) / 100

        return (
          <g key={JSON.stringify(r) + i}>
            {points.map((point, j) => {
              const pointShouldRender =
                shouldLabel &&
                nodeLabelSearchTerms.some((term) => point.id?.includes(term))
              const screenPoint = studioToScreen.point(point, zoom)
              const additionalProps = makeProps?.({
                point,
                groupIdx: i,
                memberIdx: j,
              })

              return (
                <Fragment key={JSON.stringify(point) + j}>
                  <circle
                    key={JSON.stringify(point) + j}
                    cx={screenPoint.x}
                    cy={screenPoint.y}
                    r={r}
                    opacity={opacity}
                    fill="white"
                    stroke="black"
                    {...additionalProps}
                  />
                  {pointShouldRender ? (
                    <CoordinateTooltip
                      says={point.id ?? ``}
                      where={screenPoint}
                    />
                  ) : null}
                </Fragment>
              )
            })}
          </g>
        )
      })}
    </>
  )
}

export const RainbowNodeGroups: FC<{
  nodeGroups: (Partial<Identified> & Point2d)[][]
  shouldLabel?: boolean
}> = ({ nodeGroups, shouldLabel = true }) => {
  return (
    <NodeGroups
      nodeGroups={nodeGroups}
      shouldLabel={shouldLabel}
      makeProps={({ groupIdx }) => {
        const fill = getRainbow(groupIdx)
        const r = (length - groupIdx + 1) * 3
        const opacity = (90 - r * 2) / 100

        return {
          opacity,
          fill,
          r,
        }
      }}
    />
  )
}
